function _createForOfIteratorHelper(r, e) { var t = "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (!t) { if (Array.isArray(r) || (t = _unsupportedIterableToArray(r)) || e && r && "number" == typeof r.length) { t && (r = t); var _n = 0, F = function F() {}; return { s: F, n: function n() { return _n >= r.length ? { done: !0 } : { done: !1, value: r[_n++] }; }, e: function e(r) { throw r; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var o, a = !0, u = !1; return { s: function s() { t = t.call(r); }, n: function n() { var r = t.next(); return a = r.done, r; }, e: function e(r) { u = !0, o = r; }, f: function f() { try { a || null == t["return"] || t["return"](); } finally { if (u) throw o; } } }; }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
import { ExperimentalFeature, isExperimentalFeatureEnabled, safeTruncate } from '@datadog/browser-core';
import { getNodeSelfPrivacyLevel, getPrivacySelector, NodePrivacyLevel, shouldMaskNode } from '../privacy';
import { isElementNode } from '../../browser/htmlDomUtils';
/**
 * Get the action name from the attribute 'data-dd-action-name' on the element or any of its parent.
 * It can also be retrieved from a user defined attribute.
 */
export var DEFAULT_PROGRAMMATIC_ACTION_NAME_ATTRIBUTE = 'data-dd-action-name';
export var ACTION_NAME_PLACEHOLDER = 'Masked Element';
export function getActionNameFromElement(element, _ref) {
  var enablePrivacyForActionName = _ref.enablePrivacyForActionName,
    userProgrammaticAttribute = _ref.actionNameAttribute;
  var nodePrivacyLevel = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : NodePrivacyLevel.ALLOW;
  // Proceed to get the action name in two steps:
  // * first, get the name programmatically, explicitly defined by the user.
  // * then, if privacy is set to mask, return a placeholder for the undefined.
  // * if privacy is not set to mask, use strategies that are known to return good results.
  //   Those strategies will be used on the element and a few parents, but it's likely that they won't succeed at all.
  // * if no name is found this way, use strategies returning less accurate names as a fallback.
  //   Those are much likely to succeed.
  var defaultActionName = getActionNameFromElementProgrammatically(element, DEFAULT_PROGRAMMATIC_ACTION_NAME_ATTRIBUTE) || userProgrammaticAttribute && getActionNameFromElementProgrammatically(element, userProgrammaticAttribute);
  if (defaultActionName) {
    return {
      name: defaultActionName,
      nameSource: "custom_attribute" /* ActionNameSource.CUSTOM_ATTRIBUTE */
    };
  } else if (nodePrivacyLevel === NodePrivacyLevel.MASK) {
    return {
      name: ACTION_NAME_PLACEHOLDER,
      nameSource: "mask_placeholder" /* ActionNameSource.MASK_PLACEHOLDER */
    };
  }
  return getActionNameFromElementForStrategies(element, userProgrammaticAttribute, priorityStrategies, enablePrivacyForActionName) || getActionNameFromElementForStrategies(element, userProgrammaticAttribute, fallbackStrategies, enablePrivacyForActionName) || {
    name: '',
    nameSource: "blank" /* ActionNameSource.BLANK */
  };
}
function getActionNameFromElementProgrammatically(targetElement, programmaticAttribute) {
  // We don't use getActionNameFromElementForStrategies here, because we want to consider all parents,
  // without limit. It is up to the user to declare a relevant naming strategy.
  var elementWithAttribute = targetElement.closest("[".concat(programmaticAttribute, "]"));
  if (!elementWithAttribute) {
    return;
  }
  var name = elementWithAttribute.getAttribute(programmaticAttribute);
  return truncate(normalizeWhitespace(name.trim()));
}
var priorityStrategies = [
// associated LABEL text
function (element, userProgrammaticAttribute, privacyEnabledActionName) {
  if ('labels' in element && element.labels && element.labels.length > 0) {
    return getActionNameFromTextualContent(element.labels[0], userProgrammaticAttribute, privacyEnabledActionName);
  }
},
// INPUT button (and associated) value
function (element) {
  if (element.nodeName === 'INPUT') {
    var input = element;
    var type = input.getAttribute('type');
    if (type === 'button' || type === 'submit' || type === 'reset') {
      return {
        name: input.value,
        nameSource: "text_content" /* ActionNameSource.TEXT_CONTENT */
      };
    }
  }
},
// BUTTON, LABEL or button-like element text
function (element, userProgrammaticAttribute, privacyEnabledActionName) {
  if (element.nodeName === 'BUTTON' || element.nodeName === 'LABEL' || element.getAttribute('role') === 'button') {
    return getActionNameFromTextualContent(element, userProgrammaticAttribute, privacyEnabledActionName);
  }
}, function (element) {
  return getActionNameFromStandardAttribute(element, 'aria-label');
},
// associated element text designated by the aria-labelledby attribute
function (element, userProgrammaticAttribute, privacyEnabledActionName) {
  var labelledByAttribute = element.getAttribute('aria-labelledby');
  if (labelledByAttribute) {
    return {
      name: labelledByAttribute.split(/\s+/).map(function (id) {
        return getElementById(element, id);
      }).filter(function (label) {
        return Boolean(label);
      }).map(function (element) {
        return getTextualContent(element, userProgrammaticAttribute, privacyEnabledActionName);
      }).join(' '),
      nameSource: "text_content" /* ActionNameSource.TEXT_CONTENT */
    };
  }
}, function (element) {
  return getActionNameFromStandardAttribute(element, 'alt');
}, function (element) {
  return getActionNameFromStandardAttribute(element, 'name');
}, function (element) {
  return getActionNameFromStandardAttribute(element, 'title');
}, function (element) {
  return getActionNameFromStandardAttribute(element, 'placeholder');
},
// SELECT first OPTION text
function (element, userProgrammaticAttribute, privacyEnabledActionName) {
  if ('options' in element && element.options.length > 0) {
    return getActionNameFromTextualContent(element.options[0], userProgrammaticAttribute, privacyEnabledActionName);
  }
}];
var fallbackStrategies = [function (element, userProgrammaticAttribute, privacyEnabledActionName) {
  return getActionNameFromTextualContent(element, userProgrammaticAttribute, privacyEnabledActionName);
}];
/**
 * Iterates over the target element and its parent, using the strategies list to get an action name.
 * Each strategies are applied on each element, stopping as soon as a non-empty value is returned.
 */
var MAX_PARENTS_TO_CONSIDER = 10;
function getActionNameFromElementForStrategies(targetElement, userProgrammaticAttribute, strategies, privacyEnabledActionName) {
  var element = targetElement;
  var recursionCounter = 0;
  while (recursionCounter <= MAX_PARENTS_TO_CONSIDER && element && element.nodeName !== 'BODY' && element.nodeName !== 'HTML' && element.nodeName !== 'HEAD') {
    var _iterator = _createForOfIteratorHelper(strategies),
      _step;
    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var strategy = _step.value;
        var actionName = strategy(element, userProgrammaticAttribute, privacyEnabledActionName);
        if (actionName) {
          var name = actionName.name,
            nameSource = actionName.nameSource;
          var trimmedName = name && name.trim();
          if (trimmedName) {
            return {
              name: truncate(normalizeWhitespace(trimmedName)),
              nameSource: nameSource
            };
          }
        }
      }
      // Consider a FORM as a contextual limit to get the action name.  This is experimental and may
      // be reconsidered in the future.
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
    if (element.nodeName === 'FORM') {
      break;
    }
    element = element.parentElement;
    recursionCounter += 1;
  }
}
function normalizeWhitespace(s) {
  return s.replace(/\s+/g, ' ');
}
function truncate(s) {
  return s.length > 100 ? "".concat(safeTruncate(s, 100), " [...]") : s;
}
function getElementById(refElement, id) {
  // Use the element ownerDocument here, because tests are executed in an iframe, so
  // document.getElementById won't work.
  return refElement.ownerDocument ? refElement.ownerDocument.getElementById(id) : null;
}
function getActionNameFromStandardAttribute(element, attribute) {
  return {
    name: element.getAttribute(attribute) || '',
    nameSource: "standard_attribute" /* ActionNameSource.STANDARD_ATTRIBUTE */
  };
}
function getActionNameFromTextualContent(element, userProgrammaticAttribute, privacyEnabledActionName) {
  return {
    name: getTextualContent(element, userProgrammaticAttribute, privacyEnabledActionName) || '',
    nameSource: "text_content" /* ActionNameSource.TEXT_CONTENT */
  };
}
function getTextualContent(element, userProgrammaticAttribute, privacyEnabledActionName) {
  if (element.isContentEditable) {
    return;
  }
  if (isExperimentalFeatureEnabled(ExperimentalFeature.USE_TREE_WALKER_FOR_ACTION_NAME)) {
    return getTextualContentWithTreeWalker(element, userProgrammaticAttribute, privacyEnabledActionName);
  }
  if ('innerText' in element) {
    var text = element.innerText;
    var removeTextFromElements = function removeTextFromElements(query) {
      var list = element.querySelectorAll(query);
      for (var index = 0; index < list.length; index += 1) {
        var _element = list[index];
        if ('innerText' in _element) {
          var textToReplace = _element.innerText;
          if (textToReplace && textToReplace.trim().length > 0) {
            text = text.replace(textToReplace, '');
          }
        }
      }
    };
    // remove the text of elements with programmatic attribute value
    removeTextFromElements("[".concat(DEFAULT_PROGRAMMATIC_ACTION_NAME_ATTRIBUTE, "]"));
    if (userProgrammaticAttribute) {
      removeTextFromElements("[".concat(userProgrammaticAttribute, "]"));
    }
    if (privacyEnabledActionName) {
      // remove the text of elements with privacy override
      removeTextFromElements("".concat(getPrivacySelector(NodePrivacyLevel.HIDDEN), ", ").concat(getPrivacySelector(NodePrivacyLevel.MASK)));
    }
    return text;
  }
  return element.textContent;
}
function getTextualContentWithTreeWalker(element, userProgrammaticAttribute, privacyEnabledActionName) {
  var walker = document.createTreeWalker(element,
  // eslint-disable-next-line no-bitwise
  NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, rejectInvisibleOrMaskedElementsFilter);
  var text = '';
  while (walker.nextNode()) {
    var node = walker.currentNode;
    if (isElementNode(node)) {
      if (
      // Following InnerText rendering spec https://html.spec.whatwg.org/multipage/dom.html#rendered-text-collection-steps
      node.nodeName === 'BR' || node.nodeName === 'P' || ['block', 'flex', 'grid', 'list-item', 'table', 'table-caption'].includes(getComputedStyle(node).display)) {
        text += ' ';
      }
      continue; // skip element nodes
    }
    text += node.textContent || '';
  }
  return text.replace(/\s+/g, ' ').trim();
  function rejectInvisibleOrMaskedElementsFilter(node) {
    if (isElementNode(node)) {
      var nodeSelfPrivacyLevel = getNodeSelfPrivacyLevel(node);
      if (node.hasAttribute(DEFAULT_PROGRAMMATIC_ACTION_NAME_ATTRIBUTE) || userProgrammaticAttribute && node.hasAttribute(userProgrammaticAttribute) || privacyEnabledActionName && nodeSelfPrivacyLevel && shouldMaskNode(node, nodeSelfPrivacyLevel)) {
        return NodeFilter.FILTER_REJECT;
      }
      var style = getComputedStyle(node);
      if (style.visibility !== 'visible' || style.display === 'none' || style.contentVisibility && style.contentVisibility !== 'visible'
      // contentVisibility is not supported in all browsers, so we need to check it
      ) {
        return NodeFilter.FILTER_REJECT;
      }
    }
    return NodeFilter.FILTER_ACCEPT;
  }
}